import { useState } from 'react';
import styled from 'styled-components';
import { FaStar, FaRegStar } from 'react-icons/fa';
import { toast } from 'react-toastify';

const ReviewForm = ({ onSubmit }) => {
  const ratingCategories = [
    { id: 'location_connectivity', label: 'Location & Connectivity' },
    { id: 'construction_quality', label: 'Construction Quality' },
    { id: 'amenities_facilities', label: 'Amenities & Facilities' },
    { id: 'developer_credibility', label: 'Developer Credibility' },
    { id: 'public_transport', label: 'Public Transport' },
    { id: 'community_vibe', label: 'Community Vibe' }
  ];

  const [review, setReview] = useState({
    review_text: '',
    rating: 0, // Overall rating
    parameters: Object.fromEntries(
      ratingCategories.map(cat => [cat.id, 0])
    )
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleRatingChange = (category, rating) => {
    setReview(prev => ({
      ...prev,
      parameters: {
        ...prev.parameters,
        [category]: rating
      }
    }));
  };

  const handleOverallRating = (rating) => {
    setReview(prev => ({
      ...prev,
      rating: rating
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await onSubmit(review);
      toast.success('Review submitted successfully!');
      setReview({
        review_text: '',
        rating: 0,
        parameters: Object.fromEntries(
          ratingCategories.map(cat => [cat.id, 0])
        )
      });
    } catch (error) {
      toast.error('Failed to submit review');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <CompactForm onSubmit={handleSubmit}>
      <FormTitle>Share Your Experience</FormTitle>
      
      {/* Overall Rating */}
      <RatingSection>
        <RatingLabel>Overall Rating</RatingLabel>
        <StarRating>
          {[1, 2, 3, 4, 5].map(star => (
            <StarButton
              key={`overall-${star}`}
              type="button"
              onClick={() => handleOverallRating(star)}
              $active={star <= review.rating}
            >
              {star <= review.rating ? <BoldStar /> : <FaRegStar />}
            </StarButton>
          ))}
        </StarRating>
        <RatingValue>{review.rating > 0 ? `${review.rating} out of 5` : 'Not rated'}</RatingValue>
      </RatingSection>

      {/* Parameter Ratings */}
      <RatingsGrid>
        {ratingCategories.map(category => (
          <RatingRow key={category.id}>
            <CategoryLabel>{category.label}</CategoryLabel>
            <StarRating>
              {[1, 2, 3, 4, 5].map(star => (
                <StarButton
                  key={`${category.id}-${star}`}
                  type="button"
                  onClick={() => handleRatingChange(category.id, star)}
                  $active={star <= review.parameters[category.id]}
                >
                  {star <= review.parameters[category.id] ? <FaStar /> : <FaRegStar />}
                </StarButton>
              ))}
            </StarRating>
          </RatingRow>
        ))}
      </RatingsGrid>

      <Textarea
        value={review.review_text}
        onChange={(e) => setReview({...review, review_text: e.target.value})}
        placeholder="Share your detailed thoughts..."
        disabled={isSubmitting}
        required
      />

      <SubmitButton type="submit" disabled={isSubmitting}>
        {isSubmitting ? 'Submitting...' : 'Submit Review'}
      </SubmitButton>
    </CompactForm>
  );
};

// Styled Components
const CompactForm = styled.form`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  max-width: 600px;
`;

const FormTitle = styled.h3`
  font-size: 1.1rem;
  color: #333;
  margin-bottom: 1.25rem;
  font-weight: 600;
`;

const RatingSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f0f0f0;
`;

const RatingLabel = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: #333;
  min-width: 100px;
`;

const RatingsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 1.25rem;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const RatingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const CategoryLabel = styled.span`
  font-size: 0.8rem;
  color: #555;
  min-width: 120px;
`;

const StarRating = styled.div`
  display: flex;
  gap: 0.25rem;
`;

const StarButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${props => props.$active ? '#FFD700' : '#e4e5e9'};
  font-size: 0.9rem;
  display: flex;
  align-items: center;
`;

const BoldStar = styled(FaStar)`
  font-weight: bold;
  font-size: 1.1rem;
`;

const RatingValue = styled.span`
  font-size: 0.85rem;
  color: #666;
  margin-left: auto;
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.85rem;
  margin-bottom: 1.25rem;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #4a6bff;
  }
`;

const SubmitButton = styled.button`
  background: #4a6bff;
  color: white;
  border: none;
  padding: 0.6rem 1.25rem;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
  width: 100%;

  &:hover {
    background: #3a5bef;
  }

  &:disabled {
    background: #cbd5e0;
    cursor: not-allowed;
  }
`;

export default ReviewForm;